<script setup lang="ts">
import type { Form, FormErrorEvent, FormSubmitEvent } from '#ui/types';
import { adaptFormDataToDto } from '@lib/shared';
import lodashSnakeCase from 'lodash-es/snakeCase';
import { z } from 'zod';

import { STORYBLOK_ROUTES } from '#shared/constants/storyblok';
import { type BackendErrorEntity, type CompleteOnboardingSessionBodyDto, type ErrorType, useApiCompleteOnboardingSession } from '~/apiClient';
import type { AuthGatedAction } from '~/components/Auth/AuthGatedActionInfo.vue';
import { AUTOCOMPLETE_OFF } from '~/constants/form';

interface Props {
    action?: AuthGatedAction;
}

// PROPS
defineProps<Props>();

// EMITS
const emit = defineEmits(['success']);

// COMPOSABLES
const vexatiousClauses = useStoryblokVexatiousClauses({
    slug: STORYBLOK_ROUTES.TERMS_AND_CONDITIONS,
});
const { tt } = useTypedI18n();
const { notifyBackendError, notifyGenericError, notifySuccess } = useNotification();
const { trackSignupFieldClick, trackSignupFormError, trackSignupCreateClick } = useTracking();
const { getFormErrors } = useBackendErrors();
const { logError, logInfo } = useLogs();
const { mutateAsync, isPending } = useApiCompleteOnboardingSession();
const { data: authData } = useAuthUtils();
const { countries } = useCountries();

// DATA
const formSchema = z
    .object({
        country: z.object(
            {
                countryCode: z.string(),
                taxIdMandatory: z.boolean().optional(),
            },
            { error: tt('validation.msg.required', { field: tt('signup.fields.country') }) }
        ),
        tin: z.string().optional(),
        businessName: zodRequiredString(tt('validation.msg.required', { field: tt('signup.fields.businessName') })),
        vexatiousClauses: z.literal<boolean>(true, {
            error: tt('validation.msg.required', { field: tt('signup.fields.consent') }),
        }),
    })
    .superRefine(({ country, tin }, ctx) => {
        if (!country?.taxIdMandatory || !!tin) return;

        ctx.addIssue({
            code: 'custom',
            message: tt('validation.msg.required', { field: tt('signup.fields.tin.base') }),
            path: ['tin'],
        });
    });
type FormSchema = typeof formSchema;
type FormData = z.infer<FormSchema>;

// Users created by an admin already have a company: prefill its data and lock the identifying fields (country, VAT ID);
// the business name stays editable — a corrected value is persisted on the company by the backend
const preAssignedCompany = computed(() => authData.value?.user?.company);

const getInitialFormData = (): Partial<FormData> => {
    const company = preAssignedCompany.value;
    const country = company ? countries.value.find(c => c.countryCode === company.countryCode) : undefined;

    return {
        country: country ? { countryCode: country.countryCode, taxIdMandatory: country.taxIdMandatory } : undefined,
        tin: company?.tin ?? undefined,
        businessName: company?.businessName ?? undefined,
        vexatiousClauses: false,
    };
};

const formData = reactive<Partial<FormData>>(getInitialFormData());
const form = useTemplateRef<Form<FormSchema>>('form');

// WATCHERS
watch(
    () => formData.country,
    () => {
        formData.tin = undefined;
        formData.businessName = undefined;
    }
);

// HANDLERS
const onSubmit = async (event: FormSubmitEvent<FormData>): Promise<void> => {
    const { data } = event;
    const body: CompleteOnboardingSessionBodyDto = adaptFormDataToDto({
        businessName: data.businessName,
        countryCode: data.country.countryCode,
        tin: data.tin,
        vexatiousClauses: data.vexatiousClauses,
    });

    mutateAsync({ data: body })
        .then(() => {
            notifySuccess({ title: tt('signup.onboarding.success.title'), description: tt('signup.onboarding.success.description') });
            emit('success');
        })
        .catch((apiError: ErrorType<BackendErrorEntity>) => {
            logError('Error during complete onboarding', {
                apiError: apiError.data,
                body,
            });

            if (!isBackendError(apiError.data)) {
                notifyGenericError();

                return;
            }

            const { error } = apiError.data;

            notifyBackendError({
                error,
            });

            const formErrors = getFormErrors<CompleteOnboardingSessionBodyDto, FormData>(apiError, {
                businessName: 'businessName',
                countryCode: 'country',
                tin: 'tin',
                vexatiousClauses: 'vexatiousClauses',
            });
            form.value?.setErrors(formErrors);
            nextTick().then(() => scrollToFormError());
        });
};

const onError = (errorEvent: FormErrorEvent): void => {
    logInfo('Error before complete onboarding', {
        formErrors: errorEvent.errors,
        formData,
    });

    errorEvent.errors.forEach(e => {
        trackSignupFormError(lodashSnakeCase(`${e.name}_invalid`));
    });

    if (!errorEvent.errors.length) return;

    scrollToFormError();
};
</script>

<template>
    <UForm ref="form" class="flex flex-col items-center gap-4" :state="formData" :schema="formSchema" @submit="onSubmit" @error="onError">
        <p class="text-muted text-center font-medium">{{ tt('signup.onboarding.title') }}</p>

        <UFormField class="w-full" :label="tt('signup.fields.country')" name="country" required>
            <BaseCountrySelect
                v-model="formData.country"
                :disabled="isPending || !!preAssignedCompany"
                :autocomplete="AUTOCOMPLETE_OFF"
                @click="trackSignupFieldClick('country')"
            />
        </UFormField>

        <Transition
            enter-from-class="opacity-0"
            leave-to-class="opacity-0"
            enter-active-class="transition-opacity duration-300"
            leave-active-class="transition-opacity duration-[0]"
            mode="out-in"
        >
            <UFormField
                v-if="!formData.country || formData.country?.taxIdMandatory"
                class="w-full"
                :label="tt('signup.fields.tin.base')"
                name="tin"
                required
            >
                <UInput
                    v-model.trim="formData.tin"
                    :placeholder="tt('signup.fields.tin.placeholder')"
                    :disabled="isPending || !!preAssignedCompany?.tin"
                    @input-click="trackSignupFieldClick('company_tin')"
                />
            </UFormField>
        </Transition>

        <UFormField class="w-full" :label="tt('signup.fields.businessName')" name="businessName" required>
            <UInput v-model.trim="formData.businessName" :disabled="isPending" @input-click="trackSignupFieldClick('company_business_name')" />
        </UFormField>

        <UFormField name="vexatiousClauses">
            <UCheckbox
                v-model="formData.vexatiousClauses"
                class="my-2 md:my-4"
                name="vexatiousClauses"
                aria-required="true"
                :disabled="isPending"
                @click="trackSignupFieldClick('vexatious_clauses')"
            >
                <template #label>
                    <span class="after:text-error-500 text-base after:content-['*']">{{ tt('signup.fields.vexatiousClauses') }}</span>
                    <p v-if="vexatiousClauses" class="mt-1 max-h-32 overflow-x-hidden overflow-y-scroll text-base font-normal md:mt-2 md:max-h-40">
                        {{ vexatiousClauses }}
                    </p>
                </template>
            </UCheckbox>
        </UFormField>

        <UButton :label="tt('signup.createAccount')" type="submit" size="xl" block :loading="isPending" @click="trackSignupCreateClick" />
    </UForm>
</template>
